// Lazy-loaded Google Analytics to reduce initial bundle size
let loaded = false;
const queue: any[] = [];

const hasAnalyticsConsent = () => {
  try {
    const raw = localStorage.getItem('cookie-consent');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed.analytics;
  } catch {
    return false;
  }
};

function loadScript(src: string) {
  return new Promise<void>((res, rej) => {
    const s = document.createElement('script');
    s.async = true;
    s.defer = true;
    s.src = src;
    s.onload = () => res();
    s.onerror = () => rej(new Error('GA load failed'));
    document.head.appendChild(s);
  });
}

function onIdle(cb: () => void) {
  const w: any = window;
  if (w.requestIdleCallback) return w.requestIdleCallback(cb);
  return setTimeout(cb, 0);
}

export async function ensureGA() {
  if (loaded || typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  await new Promise<void>((r) => onIdle(() => r()));

  const id = 'G-0K468V6N8Z';
  if (!id) return;

  await loadScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).gtag = function gtag(){ (window as any).dataLayer.push(arguments); };
  (window as any).gtag('js', new Date());
  (window as any).gtag('config', id, { send_page_view: false });

  loaded = true;
  while (queue.length) (window as any).gtag(...queue.shift());
}

export function track(...args: any[]) {
  const gtag = (window as any).gtag;
  if (!gtag) { queue.push(args); return; }
  gtag(...args);
}

export function trackPageview(path?: string) {
  const page_path = path ?? (typeof location !== 'undefined' ? location.pathname + location.search : '/');
  track('event', 'page_view', { page_path });
}

// Legacy compatibility functions
export const initAnalytics = () => {
  if (hasAnalyticsConsent()) {
    setTimeout(ensureGA, 100);
  }
};

export const trackEvent = async (
  eventName: string,
  parameters: Record<string, unknown> = {}
) => {
  if (!hasAnalyticsConsent()) return;
  track('event', eventName, {
    custom_map: { metric1: 'dimension1' },
    ...parameters,
  });
};

export const trackConversion = async (conversionId: string, value?: number) => {
  if (!hasAnalyticsConsent()) return;
  track('event', 'conversion', {
    send_to: conversionId,
    value: value,
    currency: 'COP',
  });
};

export const trackFormSubmit = (formType: 'contact' | 'newsletter' | 'lead') => {
  trackEvent('form_submit', {
    event_category: 'engagement',
    event_label: formType,
    form_type: formType,
  });
};

export const trackCTAClick = (ctaLocation: string, ctaText: string) => {
  trackEvent('cta_click', {
    event_category: 'cta',
    event_label: ctaLocation,
    cta_location: ctaLocation,
    cta_text: ctaText,
  });
};

export const trackServiceInterest = (service: string) => {
  trackEvent('service_interest', {
    event_category: 'services',
    event_label: service,
    service_type: service,
  });
};

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}